import { useEffect, useState } from 'react'

export interface RideAttraction {
  attraction_id: number
  attraction_name: string
  attraction_type: string
  loading_type: string | null
  total_scans: number
  unique_guests: number
  credit_scans: number
  ulr_scans: number
  fp_scans: number
  credits_collected: number
  dollar_revenue: number
  repeat_rate: number
  estimated_earnings: number
  est_earnings_per_guest: number | null
  est_earnings_per_scan: number | null
  avg_ulr_scan_value: number | null
  avg_fp_scan_value: number | null
  breakage_rate: number | null
}

export interface RideClassification {
  attraction_id: number
  attraction_name: string
  attraction_type: string
  loading_type: string | null
  max_capacity_per_hour: number
  max_capacity_per_cycle: number | null
  seat_capacity: number | null
  avg_cycle_time_sec: number | null
  avg_load_time_sec: number | null
  avg_ride_time_sec: number | null
  price_per_scan: number | null
  classified_at: string | null
}

export interface ScanValues {
  ulr: { total_revenue: number; total_scans: number; avg_scan_value: number | null }
  fast_pass: { total_revenue: number; total_scans: number; avg_scan_value: number | null }
  breakage: {
    credits_loaded: number
    credits_spent: number
    breakage_credits: number
    breakage_rate: number | null
  }
}

export interface RankingEntry {
  rank: number
  attraction_id: number
  attraction_name: string
  attraction_type: string
  value: number
}

export interface RankingDimension {
  label: string
  top_5: RankingEntry[]
  total_ranked: number
}

export interface AttractionRecord {
  attraction_id: number
  attraction_name: string
  record_type: string
  previous_value: number
  new_value: number
  improvement: number
  event_id: number
  event_name: string
  recorded_date: string
  recorded_at: string
}

export interface DimensionMeta {
  key: string
  label: string
}

export interface TypeSplitEntry {
  count: number
  total_scans: number
  unique_guests: number
  estimated_earnings: number
  credit_scans: number
  ulr_scans: number
}

export interface RideIntelligenceData {
  source: string
  event_id: number
  attractions: RideAttraction[]
  rankings: Record<string, RankingDimension>
  type_split: Record<string, TypeSplitEntry>
  classifications: RideClassification[]
  scan_values: ScanValues | null
  records: AttractionRecord[]
  dimensions: DimensionMeta[]
  generated_at: string
}

export function useRideIntelligence(eventId: number | null) {
  const [data, setData] = useState<RideIntelligenceData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!eventId || !Number.isFinite(eventId)) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)

    fetch(`/api/dashboard/${eventId}/ride-intelligence`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? (r.json() as Promise<RideIntelligenceData>) : null))
      .then((d) => {
        if (!cancelled) {
          setData(d ?? null)
          setLoading(false)
        }
      })
      .catch(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [eventId])

  return { data, loading }
}
